import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  LocalShippingOutlined as TruckIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { PlateInput } from '../PlateInput';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';
import {
  CapacityUnit,
  EngineType,
  TruckStatus,
} from '../../types/transportation.type';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
} as const;

const formGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 2,
  '& > .full-width': { gridColumn: '1 / -1' },
} as const;

const capacityRowSx = {
  display: 'grid',
  gridTemplateColumns: '1fr 180px',
  gap: 2,
  gridColumn: '1 / -1',
} as const;

const TRUCK_FEATURE_PRESETS = [
  'Compactor',
  'Refrigerated',
  'Tail lift',
  'Hook lift',
  'Crane',
  'Side loader',
  'Flatbed',
  'Open top',
] as const;

const SAR_SYMBOL_SRC = '/images/Saudi_Riyal_Symbol.svg';

function normalizeTruckFeatures(next: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of next) {
    const s = String(raw).trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

function FormSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        fontWeight: 600,
        letterSpacing: 0,
        display: 'block',
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

export type VehicleFormState = {
  name: string;
  capacity: number;
  status: TruckStatus;
  data: {
    en_plate_number: string;
    ar_plate_number: string;
    car_model: string;
    vehicle_type: string;
    capacity_unit: CapacityUnit | null | undefined;
    empty_weight: number | undefined;
    fuel_consumption_km_per_l: number | undefined;
    engine_type: EngineType | null | undefined;
    vin: string;
    price_per_trip: number | undefined;
    truck_features: string[];
  };
};

export interface VehicleFormDialogProps {
  open: boolean;
  onClose: () => void;
  isEdit: boolean;
  formData: VehicleFormState;
  setFormData: React.Dispatch<React.SetStateAction<VehicleFormState>>;
  formErrors: Record<string, string>;
  loading: boolean;
  /** Whether the current user is permitted to submit this form (add or edit). */
  canSubmit: boolean;
  onSubmit: (event: React.FormEvent) => void;
  onPlateError: (message: string) => void;
  tourFormHint?: boolean;
  tourSubmitHint?: boolean;
  onTourFormNext?: () => void;
  onTourSubmitDone?: () => void;
  onTourDismiss?: () => void;
}

const VehicleFormDialog: React.FC<VehicleFormDialogProps> = ({
  open,
  onClose,
  isEdit,
  formData,
  setFormData,
  formErrors,
  loading,
  canSubmit,
  onSubmit,
  onPlateError,
  tourFormHint = false,
  tourSubmitHint = false,
  onTourFormNext,
  onTourSubmitDone,
  onTourDismiss,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const featureAutocompleteOptions = useMemo(() => {
    const selected = formData.data.truck_features ?? [];
    const customs = selected.filter(
      (f) => !TRUCK_FEATURE_PRESETS.some((p) => p.toLowerCase() === f.toLowerCase())
    );
    return [...TRUCK_FEATURE_PRESETS, ...customs];
  }, [formData.data.truck_features]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            <TruckIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}>
              {isEdit
                ? t('transportation.fleet.editDialogTitle')
                : t('transportation.fleet.addDialogTitle')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25 }}
            >
              {isEdit ? t('common.edit') : t('common.add')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Box component="form" onSubmit={onSubmit} noValidate>
        <DialogContent sx={{ px: 2.5, py: 2 }}>
          <SetupCoachmark
            open={tourFormHint && open && !isEdit}
            fullWidth
            title={t('selfSetup.tour.vehiclesFormTitle')}
            subtitle={t('selfSetup.tour.vehiclesFormSubtitle')}
            nextLabel={t('selfSetup.tourNext')}
            onNext={() => onTourFormNext?.()}
            onDismiss={() => onTourDismiss?.()}
          >
            <Stack spacing={3} divider={<Divider flexItem sx={{ opacity: 0.55 }} />}>
              <Box>
                <FormSectionTitle>{t('transportation.fleet.formSections.plate')}</FormSectionTitle>
              <PlateInput
                value={formData.data}
                onChange={(val) =>
                  setFormData({
                    ...formData,
                    data: { ...formData.data, ...val },
                  })
                }
                onError={onPlateError}
              />
            </Box>

            <Box>
              <FormSectionTitle>{t('transportation.fleet.formSections.vehicle')}</FormSectionTitle>
              <Box sx={formGridSx}>
                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('transportation.fleet.vehicleName')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  select
                  label={t('transportation.fleet.vehicleType')}
                  value={formData.data.vehicle_type || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, vehicle_type: e.target.value },
                    })
                  }
                >
                  <MenuItem value="">{t('common.none')}</MenuItem>
                  <MenuItem value="truck">{t('transportation.fleet.vehicleTypes.truck')}</MenuItem>
                  <MenuItem value="van">{t('transportation.fleet.vehicleTypes.van')}</MenuItem>
                  <MenuItem value="car">{t('transportation.fleet.vehicleTypes.car')}</MenuItem>
                  <MenuItem value="bus">{t('transportation.fleet.vehicleTypes.bus')}</MenuItem>
                  <MenuItem value="trailer">{t('transportation.fleet.vehicleTypes.trailer')}</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('transportation.fleet.carModel')}
                  value={formData.data.car_model}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, car_model: e.target.value },
                    })
                  }
                  placeholder={t('transportation.fleet.carModelPlaceholder')}
                />

                <Box sx={capacityRowSx}>
                  <TextField
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    label={t('common.capacity')}
                    type="number"
                    value={formData.capacity || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        capacity: Number(e.target.value),
                      })
                    }
                    required
                    error={!!formErrors.capacity}
                    helperText={formErrors.capacity}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    select
                    label={t('transportation.fleet.capacityUnit')}
                    value={formData.data.capacity_unit || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        data: {
                          ...formData.data,
                          capacity_unit: (e.target.value || undefined) as CapacityUnit | undefined,
                        },
                      })
                    }
                  >
                    <MenuItem value="">{t('common.none')}</MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                    <MenuItem value="ton">ton</MenuItem>
                    <MenuItem value="lb">lb</MenuItem>
                  </TextField>
                </Box>
              </Box>
            </Box>

            <Box>
              <FormSectionTitle>{t('transportation.fleet.formSections.specs')}</FormSectionTitle>
              <Box sx={formGridSx}>
                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('transportation.fleet.emptyWeight')}
                  type="number"
                  value={formData.data.empty_weight ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        empty_weight:
                          e.target.value === '' ? undefined : Number(e.target.value),
                      },
                    })
                  }
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('transportation.fleet.fuelConsumption')}
                  type="number"
                  value={formData.data.fuel_consumption_km_per_l ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        fuel_consumption_km_per_l:
                          e.target.value === '' ? undefined : Number(e.target.value),
                      },
                    })
                  }
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  select
                  label={t('transportation.fleet.engineType')}
                  value={formData.data.engine_type || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        engine_type: (e.target.value || undefined) as EngineType | undefined,
                      },
                    })
                  }
                >
                  <MenuItem value="">{t('common.none')}</MenuItem>
                  <MenuItem value="diesel">{t('transportation.fleet.engineTypes.diesel')}</MenuItem>
                  <MenuItem value="petrol">{t('transportation.fleet.engineTypes.petrol')}</MenuItem>
                  <MenuItem value="hybrid">{t('transportation.fleet.engineTypes.hybrid')}</MenuItem>
                  <MenuItem value="electric">{t('transportation.fleet.engineTypes.electric')}</MenuItem>
                  <MenuItem value="cng">{t('transportation.fleet.engineTypes.cng')}</MenuItem>
                  <MenuItem value="other">{t('transportation.fleet.engineTypes.other')}</MenuItem>
                </TextField>

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('transportation.fleet.pricePerTrip')}
                  type="number"
                  value={formData.data.price_per_trip ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        price_per_trip:
                          e.target.value === '' ? undefined : Number(e.target.value),
                      },
                    })
                  }
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          component="img"
                          src={SAR_SYMBOL_SRC}
                          alt=""
                          sx={(t) => ({
                            width: 22,
                            height: 'auto',
                            display: 'block',
                            ...(t.palette.mode === 'dark'
                              ? { opacity: 0.5, filter: 'brightness(0) invert(1)' }
                              : { opacity: 0.48, filter: 'brightness(0)' }),
                          })}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>

            <Box>
              <FormSectionTitle>{t('transportation.fleet.formSections.additional')}</FormSectionTitle>
              <Box sx={formGridSx}>
                <Autocomplete
                  className="full-width"
                  multiple
                  freeSolo
                  options={featureAutocompleteOptions}
                  value={formData.data.truck_features ?? []}
                  onChange={(_, newValue) =>
                    setFormData({
                      ...formData,
                      data: {
                        ...formData.data,
                        truck_features: normalizeTruckFeatures(newValue.map(String)),
                      },
                    })
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={option}
                        {...getTagProps({ index })}
                        key={`${option}-${index}`}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      sx={fieldSx}
                      label={t('transportation.fleet.truckFeatures')}
                      placeholder={t('transportation.fleet.truckFeaturesPlaceholder')}
                    />
                  )}
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  label={t('transportation.fleet.vin')}
                  value={formData.data.vin}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      data: { ...formData.data, vin: e.target.value },
                    })
                  }
                />

                <TextField
                  fullWidth
                  size="small"
                  sx={fieldSx}
                  select
                  label={t('common.status')}
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      status: e.target.value as TruckStatus,
                    })
                  }
                  required
                >
                  {Object.values(TruckStatus).map((status) => (
                    <MenuItem key={status} value={status}>
                      {t(`transportation.fleet.status.${status}`, { defaultValue: status })}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
            </Stack>
          </SetupCoachmark>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            {t('common.cancel')}
          </Button>
          <SetupCoachmark
            open={tourSubmitHint && open && !isEdit}
            title={t('selfSetup.tour.vehiclesSubmitTitle')}
            subtitle={t('selfSetup.tour.vehiclesSubmitSubtitle')}
            nextLabel={t('selfSetup.tourDone')}
            onNext={() => onTourSubmitDone?.()}
            onDismiss={() => onTourDismiss?.()}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !canSubmit}
              startIcon={isEdit ? <SaveIcon /> : <AddIcon />}
              sx={{ textTransform: 'none' }}
            >
              {isEdit ? t('common.update') : t('common.add')}
            </Button>
          </SetupCoachmark>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default VehicleFormDialog;
